// sogweb/src/components/InvoiceModal.tsx
import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import {
  Button,
  Box,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import {
  Print as PrintIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import api from '../api/api';
import SpiceLevelBadge from './SpiceLevelBadge';
import { printToLocalPrinter, InvoiceData as LocalInvoiceData } from '../services/localPrintService';
import { useTheme } from '../context/ThemeContext';

// Define interfaces for Invoice data
interface InvoiceItem {
  id: number;
  menuItem: {
    id: number;
    name: string;
    price: number;
    description?: string;
  };
  quantity: number;
  price: number;
  comment?: string;
  toppings?: { id: number; name: string; price?: number }[];
  selectedToppings?: { id: number; name: string; price: number }[];
  freeToppingsCount?: number;
  requestedSpiceLevel?: string;
  subtotal?: number;
}

interface Payment {
  id: number;
  amount: number;
  method: string;
  status: string;
  createdAt: string;
  transactionId?: string;
}

interface Order {
  id: number;
  customOrderId?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  deliveryType?: string;
  qrCode?: { code: string; location?: string };
  user?: { 
    id: number; 
    username: string; 
    email?: string;
    phone?: string;
  };
  guestName?: string;
  guestPhone?: string;
  guestEmail?: string;
  isGuestOrder?: boolean;
  acceptedAt?: string;
  preparingAt?: string;
  readyAt?: string;
  completedAt?: string;
}

interface ConsolidatedInvoiceResponse {
  id: number;
  createdAt: string;
  total: number;
  order: Order & {
    items: InvoiceItem[];
  };
  isConsolidated?: boolean;
  consolidatedOrders?: Array<Order & {
    items: InvoiceItem[];
  }>;
  consolidatedTotal?: number;
  orderLineId?: number;
  sessionCode?: string;
  isPreview?: boolean;
  // Payment info
  paymentMethod?: string;
  paymentNotes?: string;
  // Tax and discount fields from backend
  subtotal?: number;
  cgstRate?: number;
  cgstAmount?: number;
  sgstRate?: number;
  sgstAmount?: number;
  igstRate?: number;
  igstAmount?: number;
  serviceChargeRate?: number;
  serviceChargeAmount?: number;
  discountAmount?: number;
  discountType?: string;
  discountValue?: number;
  couponCode?: string;
  discountReason?: string;
  taxableAmount?: number;
  roundOff?: number;
  grandTotal?: number;
  // GB VAT (tax-inclusive) fields — null/absent on the GST path
  vatRate?: number;
  vatAmount?: number;
  currencyCode?: string;
}

interface Invoice {
  id: number;
  invoiceNumber?: string;
  order: Order & {
    items: InvoiceItem[];
  };
  items?: InvoiceItem[];
  createdAt: string;
  updatedAt?: string;
  dueDate?: string;
  total: number;
  subtotal?: number;
  tax?: number;
  discount?: number;
  status?: 'DRAFT' | 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED' | 'PREVIEW';
  paidAmount?: number;
  balanceAmount?: number;
  payments?: Payment[];
  notes?: string;
  terms?: string;
  vendor?: {
    id: number;
    username: string;
    businessName?: string;
    address?: string;
    phone?: string;
    email?: string;
    taxId?: string;
  };
  // Tax breakdown fields
  cgstRate?: number;
  cgstAmount?: number;
  sgstRate?: number;
  sgstAmount?: number;
  igstRate?: number;
  igstAmount?: number;
  serviceChargeRate?: number;
  serviceChargeAmount?: number;
  discountAmount?: number;
  discountType?: string;
  discountValue?: number;
  couponCode?: string;
  discountReason?: string;
  taxType?: string;
  taxableAmount?: number;
  roundOff?: number;
  grandTotal?: number;
  // GB VAT (tax-inclusive) fields — null/absent on the GST path
  vatRate?: number;
  vatAmount?: number;
  currencyCode?: string;
  // Consolidated invoice fields
  isConsolidated?: boolean;
  consolidatedOrders?: Array<Order & {
    items: InvoiceItem[];
  }>;
  consolidatedTotal?: number;
  orderLineId?: number;
  sessionCode?: string;
  isPreview?: boolean;
  // Payment method
  paymentMethod?: string;
  paymentNotes?: string;
}

interface InvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: number | null;
  invoiceId?: number | null;
  orderDisplayId?: string; // Add this for UI display
  includeCompleted?: boolean; // Include completed orders in session invoice (for payment confirmation)
}

const InvoiceModal: React.FC<InvoiceModalProps> = ({ isOpen, onClose, orderId, invoiceId, orderDisplayId, includeCompleted = false }) => {
  const { theme } = useTheme();
  const [invoiceData, setInvoiceData] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSettling, setIsSettling] = useState<boolean>(false);
  const [settlingMessage, setSettlingMessage] = useState<string>('Preparing invoice…');

  // Theme-derived colors
  const primary = theme.primary;
  const primaryHover = theme.primaryHover || primary;
  const neutral = theme.gray?.[500] || '#64748B';
  const neutralDark = theme.gray?.[700] || '#374151';

  // Currency-aware rendering (UK plan Phase 2 step 6): the backend stamps
  // currencyCode on every invoice payload. GBP shows £ with exact pence;
  // INR (and legacy payloads without the field) keeps ₹ with whole-rupee totals.
  const currencySymbol = invoiceData?.currencyCode === 'GBP' ? '£' : '₹';
  const isWholeUnitRounding = !invoiceData?.currencyCode || invoiceData.currencyCode === 'INR';

  // Fetch invoice data
  const fetchInvoice = async () => {
    if (!orderId && !invoiceId) return;
    
    setLoading(true);
    setError(null);
    setIsSettling(true);
    setSettlingMessage('Preparing invoice…');

    try {
      let response;
      if (invoiceId) {
        response = await api.get<Invoice>(`/invoices/${invoiceId}`);
      } else {
        // Always use preview mode when fetching by order ID
        // includeCompleted=true shows all session orders (for payment confirmation)
        const includeCompletedParam = includeCompleted ? '&includeCompleted=true' : '';
        response = await api.get<ConsolidatedInvoiceResponse>(`/invoices/by-order/${orderId}?preview=true${includeCompletedParam}`);
      }
      
      console.log('Invoice response:', response.data);

      // Transform the response to match Invoice interface
      // Backend now returns filtered active orders only with correct totals
      const invoiceData: Invoice = {
        ...response.data,
        id: response.data.id || 0, // Use 0 for preview invoices
        invoiceNumber: response.data.id?.toString() || 'PREVIEW',
        vendor: (response.data.order as any)?.vendor,
        items: response.data.order?.items || [],
        subtotal: response.data.subtotal, // Backend returns correct subtotal
        tax: 0,
        discount: 0,
        status: response.data.isPreview ? 'PREVIEW' : 'PAID',
        // Backend returns only active orders and correct totals
        isConsolidated: response.data.isConsolidated,
        consolidatedOrders: response.data.consolidatedOrders,
        consolidatedTotal: response.data.consolidatedTotal,
        orderLineId: response.data.orderLineId,
        sessionCode: response.data.sessionCode,
        isPreview: response.data.isPreview
      };
      
      // Keep a minimal single-step loader; no extra phases
      
      setInvoiceData(invoiceData);
      setIsSettling(false);
    } catch (err) {
      console.error("Failed to fetch invoice:", err);
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message || "Failed to load invoice details.");
      setIsSettling(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && (orderId || invoiceId)) {
      fetchInvoice();
    } else {
      // Reset state when modal closes
      setInvoiceData(null);
      setLoading(false);
      setError(null);
      setIsSettling(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderId, invoiceId]);



  // Direct browser print
  const handleDirectPrint = () => {
    window.print();
    toast.success('Opening print dialog...');
  };

  // Print via billing system (tries local printer first, then Socket.IO fallback)
  const handleBillingSystemPrint = async () => {
    if (!invoiceData) {
      toast.error("Invoice data is not available for printing.");
      return;
    }

    // Try local printer first (direct to desktop app on localhost:9100)
    const localResult = await printToLocalPrinter(invoiceData as LocalInvoiceData);

    if (localResult.success) {
      toast.success('Receipt printed via local printer');
      return;
    }

    // Local printer failed - fall back to Socket.IO method
    console.log('[InvoiceModal] Local printer unavailable, falling back to billing system:', localResult.error);

    try {
      // Call the print API endpoint which will emit PRINT_RECEIPT event via Socket.IO
      // This ensures the desktop print service receives the full receipt data
      if (invoiceData.id && invoiceData.id > 0) {
        // Use invoice endpoint for existing invoices
        const response = await api.post(`/invoices/${invoiceData.id}/print`);
        if (response.data.success) {
          toast.success('Print request sent to billing system');
        } else {
          throw new Error(response.data.message || 'Print request failed');
        }
      } else {
        // Use order endpoint for preview invoices
        // Use orderId prop as fallback if invoiceData.order?.id is not available
        const orderIdToUse = invoiceData.order?.id || orderId;
        if (orderIdToUse) {
          const response = await api.post(`/orders/${orderIdToUse}/print-receipt`, null, {
            params: {
              consolidated: invoiceData.isConsolidated || false
            }
          });
          if (response.data.success) {
            toast.success('Print request sent to billing system');
          } else {
            throw new Error(response.data.message || 'Print request failed');
          }
        } else {
          toast.error('No order ID available for printing');
        }
      }
    } catch (err) {
      console.error('Print error:', err);
      toast.error('Failed to send print request');
    }
  };



  // Unused function - commented out
  /*
  const handleEmailSend = async () => {
    if (!invoiceData?.id || !emailTo) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setEmailLoading(true);
    try {
      await api.post(`/invoices/${invoiceData.id}/email`, {
        to: emailTo,
        message: emailMessage
      });
      
      toast.success('Invoice sent successfully!');
      setActiveTab(0); // Go back to overview
    } catch (err) {
      console.error("Failed to send email:", err);
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message || "Failed to send invoice email.");
    } finally {
      setEmailLoading(false);
    }
  };
  */



  // Unused function - commented out
  /*
  const calculateSubtotal = () => {
    if (!invoiceData) return 0;
    
    // If consolidated invoice, calculate total from all orders
    if (invoiceData.isConsolidated && invoiceData.consolidatedOrders) {
      return invoiceData.consolidatedOrders.reduce((total, order) => {
        const orderItems = order.items || [];
        return total + orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      }, 0);
    }
    
    // Single order
    const items = invoiceData.items || invoiceData.order?.items || [];
    return items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };
  */

  const getStatusColor = (status?: string) => {
    const statusTheme = theme.components?.status;
    switch (status) {
      case 'PAID': return statusTheme?.PAID?.text || theme.success || '#10b981';
      case 'SENT': return statusTheme?.SENT?.text || theme.accent || '#3b82f6';
      case 'OVERDUE': return statusTheme?.OVERDUE?.text || theme.danger || '#ef4444';
      case 'CANCELLED': return statusTheme?.CANCELLED?.text || theme.gray?.[500] || '#6b7280';
      case 'PREVIEW': return statusTheme?.PREVIEW?.text || theme.accent || '#8b5cf6';
      default: return statusTheme?.PENDING?.text || theme.warning || '#f59e0b';
    }
  };

  // Unused function - commented out
  /*
  const getPaymentMethodIcon = (method: string) => {
    switch (method.toLowerCase()) {
      case 'card':
      case 'credit_card':
        return '💳';
      case 'cash':
        return '💵';
      case 'upi':
        return '📱';
      case 'wallet':
        return '👛';
      default:
        return '💰';
    }
  };
  */

  return (
    <>
      {/* Print-specific styles */}
      <style>{`
        @media print {
          /* Hide everything except the invoice content */
          body * {
            visibility: hidden;
          }

          /* Show only the invoice content */
          .invoice-print-content,
          .invoice-print-content * {
            visibility: visible;
          }

          /* Position invoice content at top of page */
          .invoice-print-content {
            position: absolute;
            left: 50%;
            top: 0;
            transform: translateX(-50%);
            width: 80mm; /* Thermal receipt width */
            padding: 2mm 5mm; /* Minimal top/bottom, side padding */
            margin: 0;
            font-family: 'Courier New', monospace; /* Monospace for receipt look */
            font-size: 9pt;
            line-height: 1.2;
            /* Prevent page breaks inside content */
            page-break-inside: avoid;
            break-inside: avoid;
          }

          /* Prevent page breaks within key sections */
          .invoice-print-content > div,
          .invoice-print-content .mb-6,
          .invoice-print-content .space-y-2 {
            page-break-inside: avoid;
            break-inside: avoid;
          }

          /* Hide UI elements during print */
          .print-hide,
          .modal-backdrop,
          .modal-header,
          button,
          .MuiButton-root {
            display: none !important;
          }

          /* Clean formatting for print */
          .invoice-print-content {
            background: white;
            color: black;
          }

          /* Make headers centered and bold */
          .invoice-print-content h3 {
            text-align: center;
            font-weight: bold;
            margin: 5px 0;
            font-size: 10pt;
          }

          /* Clean table-like formatting */
          .invoice-print-content .flex {
            display: flex !important;
            justify-content: space-between !important;
          }

          /* Remove fancy styling */
          .invoice-print-content .bg-green-50,
          .invoice-print-content .border-green-100 {
            background: white !important;
            border: 1px dashed black !important;
            padding: 2mm !important;
            margin: 2mm 0 !important;
          }

          /* Compact spacing for receipt */
          .invoice-print-content .mb-6,
          .invoice-print-content .mt-6 {
            margin-bottom: 3mm !important;
            margin-top: 3mm !important;
          }

          .invoice-print-content .space-y-2 > * {
            margin-top: 1mm !important;
            margin-bottom: 1mm !important;
          }

          /* Thermal receipt page settings - single continuous page */
          @page {
            margin: 0;
            size: 80mm auto; /* 80mm width, auto height for continuous feed */
            /* Remove default page breaks */
            orphans: 1;
            widows: 1;
          }

          /* Reset body margins for print */
          body {
            margin: 0 !important;
            padding: 0 !important;
          }

          /* Reset html for proper sizing */
          html {
            margin: 0 !important;
            padding: 0 !important;
          }

          /* Print dividers as simple lines */
          .invoice-print-content .border-t,
          .invoice-print-content .border-b {
            border-color: black !important;
            border-style: dashed !important;
            border-width: 1px !important;
          }

          /* Compact text sizes */
          .invoice-print-content .text-sm,
          .invoice-print-content .text-xs {
            font-size: 8pt !important;
            line-height: 1.2 !important;
          }

          .invoice-print-content .text-base {
            font-size: 9pt !important;
          }

          .invoice-print-content .text-lg {
            font-size: 10pt !important;
            font-weight: bold !important;
          }
        }
      `}</style>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          <div className="flex items-center gap-2">
            <ReceiptIcon style={{ fontSize: 20, color: theme.gray?.[500] || '#6b7280' }} />
            <span className="text-base font-medium" style={{ color: theme.text }}>
              Invoice {invoiceData?.invoiceNumber ? `#${invoiceData.invoiceNumber}` : ''}
            </span>
            {invoiceData?.status && (
              <span
                className="ml-2 px-2 py-0.5 text-xs font-medium rounded"
                style={{
                  backgroundColor: getStatusColor(invoiceData.status) + '15',
                  color: getStatusColor(invoiceData.status),
                }}
              >
                {invoiceData.status}
              </span>
            )}
          </div>
        }
        maxWidth="lg"
        className="sm:max-w-4xl"
        contentClassName="p-0"
        mobileFullScreen={false}
      >
      <div className="flex flex-col h-full">
        {/* Content */}
        <div className="flex-1 overflow-auto" style={{ paddingBottom: '20px' }}>
          {isSettling ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={400} gap={3}>
              <CircularProgress style={{ color: neutral }} size={48} />
              <Typography variant="body1" color="textSecondary">
                {settlingMessage}
              </Typography>
              {/* Intentionally no additional progress bars to avoid multi-step feel */}
            </Box>
          ) : loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
              <CircularProgress style={{ color: neutral }} />
            </Box>
          ) : error ? (
            <Box p={3}>
              <Alert severity="error">{error}</Alert>
              <Button
                onClick={fetchInvoice}
                variant="contained"
                className="mt-4"
                sx={{
                  backgroundColor: primary,
                  '&:hover': {
                    backgroundColor: primaryHover
                  }
                }}
              >
                Retry
              </Button>
            </Box>
          ) : !invoiceData ? (
            <Box p={3}>
              <Alert severity="info">
                No invoice found for this order.
              </Alert>
            </Box>
          ) : (
            <>
              {/* Invoice Content */}
              <Box className="p-4 sm:p-6">
                {/* Print Buttons */}
                <Box className="print-hide flex justify-center gap-3 mb-6 flex-wrap">
                  {/* Direct Browser Print */}
                  <Button
                    variant="outlined"
                    startIcon={<PrintIcon style={{ width: 18, height: 18 }} />}
                    onClick={handleDirectPrint}
                    size="medium"
                    sx={{
                      borderColor: neutral,
                      color: neutralDark,
                      px: 3,
                      py: 1.5,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '8px',
                      '&:hover': {
                        borderColor: neutralDark,
                        backgroundColor: `${neutral}14`
                      },
                      '& .MuiButton-startIcon': {
                        marginRight: 1
                      }
                    }}
                  >
                    Print
                  </Button>

                  {/* Print via Billing System (Desktop App) */}
                  <Button
                    variant="contained"
                    startIcon={<ReceiptIcon style={{ width: 18, height: 18 }} />}
                    onClick={handleBillingSystemPrint}
                    size="medium"
                    sx={{
                      backgroundColor: primary,
                      color: 'white',
                      px: 3,
                      py: 1.5,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '8px',
                      boxShadow: `0 2px 4px ${primary}33`,
                      '&:hover': {
                        backgroundColor: primaryHover,
                        boxShadow: `0 4px 8px ${primary}4D`
                      },
                      '& .MuiButton-startIcon': {
                        marginRight: 1
                      }
                    }}
                  >
                    Print via Billing System
                  </Button>
                </Box>

                <div className="invoice-print-content">
                  {/* Invoice Header - Minimal design */}
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <p className="text-sm" style={{ color: theme.gray?.[500] || '#6b7280' }}>Invoice Date</p>
                      <p className="font-medium" style={{ color: theme.text }}>
                        {format(new Date(invoiceData.createdAt), 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm" style={{ color: theme.gray?.[500] || '#6b7280' }}>Order ID</p>
                      <p className="font-medium" style={{ color: theme.text }}>
                        #{orderDisplayId || invoiceData.order?.customOrderId || invoiceData.order?.id}
                      </p>
                    </div>
                  </div>

                  {/* Order Items - Clean design */}
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold mb-3" style={{ color: theme.text }}>Order Items</h3>
                    <div className="space-y-2">
                      {invoiceData.isConsolidated && invoiceData.consolidatedOrders ? (
                        // Show all items from all consolidated orders
                        invoiceData.consolidatedOrders.map((order) => (
                          <div key={order.id}>
                            {order.items && order.items.length > 0 && (
                              <>
                                <div className="text-xs mt-3 mb-1" style={{ color: theme.gray?.[500] || '#6b7280' }}>
                                  Order #{orderDisplayId || order.customOrderId || order.id}
                                </div>
                                {order.items.map((item) => {
                                  const toppings = item.selectedToppings || item.toppings || [];
                                  const sortedToppings = [...toppings].sort((a, b) => (b.price || 0) - (a.price || 0));
                                  const freeCount = item.freeToppingsCount || 0;

                                  return (
                                    <div key={`${order.id}-${item.id}`} className="py-2 border-b last:border-0" style={{ borderColor: theme.components?.card?.border || '#E8E6E1' }}>
                                      <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2">
                                            <p className="text-sm font-medium" style={{ color: theme.text }}>{item.menuItemName}</p>
                                            {item.portionName && (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: `${theme.accent}20`, color: theme.accent }}>
                                                {item.portionName}
                                              </span>
                                            )}
                                          </div>
                                          <p className="text-xs" style={{ color: theme.gray?.[500] || '#6b7280' }}>Qty: {item.quantity} × {currencySymbol}{item.price}</p>

                                          {/* Spice Level */}
                                          {item.requestedSpiceLevel && item.requestedSpiceLevel !== 'NONE' && (
                                            <div className="mt-1">
                                              <SpiceLevelBadge spiceLevel={item.requestedSpiceLevel} size="sm" />
                                            </div>
                                          )}

                                          {/* Toppings */}
                                          {toppings.length > 0 && (
                                            <div className="mt-1 space-y-0.5">
                                              {sortedToppings.map((topping, idx) => {
                                                const isFree = idx < freeCount;
                                                const toppingPrice = topping.price || 0;
                                                return (
                                                  <div key={topping.id} className="flex items-center justify-between text-xs">
                                                    <span style={{ color: theme.gray?.[600] || '#4b5563' }}>
                                                      + {topping.name}
                                                      {isFree && <span className="ml-1 font-medium" style={{ color: theme.success }}>(Free)</span>}
                                                    </span>
                                                    <span style={{ color: isFree ? theme.success : (theme.gray?.[700] || '#374151'), textDecoration: isFree ? 'line-through' : 'none' }}>
                                                      {currencySymbol}{toppingPrice.toFixed(2)}
                                                    </span>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                        <p className="text-sm font-medium ml-4" style={{ color: theme.text }}>
                                          {currencySymbol}{(item.quantity * item.price).toFixed(2)}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </>
                            )}
                          </div>
                        ))
                      ) : (
                        // Show items from single order
                        (invoiceData.items || invoiceData.order?.items || []).map((item) => {
                          const toppings = item.selectedToppings || item.toppings || [];
                          const sortedToppings = [...toppings].sort((a, b) => (b.price || 0) - (a.price || 0));
                          const freeCount = item.freeToppingsCount || 0;

                          return (
                            <div key={item.id} className="py-2 border-b last:border-0" style={{ borderColor: theme.components?.card?.border || '#E8E6E1' }}>
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium" style={{ color: theme.text }}>{item.menuItemName}</p>
                                    {item.portionName && (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: `${theme.accent}20`, color: theme.accent }}>
                                        {item.portionName}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs" style={{ color: theme.gray?.[500] || '#6b7280' }}>Qty: {item.quantity} × {currencySymbol}{item.price}</p>

                                  {/* Spice Level */}
                                  {item.requestedSpiceLevel && item.requestedSpiceLevel !== 'NONE' && (
                                    <div className="mt-1">
                                      <SpiceLevelBadge spiceLevel={item.requestedSpiceLevel} size="sm" />
                                    </div>
                                  )}

                                  {/* Toppings */}
                                  {toppings.length > 0 && (
                                    <div className="mt-1 space-y-0.5">
                                      {sortedToppings.map((topping, idx) => {
                                        const isFree = idx < freeCount;
                                        const toppingPrice = topping.price || 0;
                                        return (
                                          <div key={topping.id} className="flex items-center justify-between text-xs">
                                            <span style={{ color: theme.gray?.[600] || '#4b5563' }}>
                                              + {topping.name}
                                              {isFree && <span className="ml-1 font-medium" style={{ color: theme.success }}>(Free)</span>}
                                            </span>
                                            <span style={{ color: isFree ? theme.success : (theme.gray?.[700] || '#374151'), textDecoration: isFree ? 'line-through' : 'none' }}>
                                              {currencySymbol}{toppingPrice.toFixed(2)}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                                <p className="text-sm font-medium ml-4" style={{ color: theme.text }}>
                                  {currencySymbol}{(item.quantity * item.price).toFixed(2)}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Tax Breakdown and Total */}
                    <div className="mt-4 pt-4 border-t space-y-2" style={{ borderColor: theme.components?.card?.border || '#E8E6E1' }}>
                      {/* Subtotal */}
                      {invoiceData.subtotal !== undefined && invoiceData.subtotal !== null && (
                        <div className="flex justify-between text-sm">
                          <span style={{ color: theme.gray?.[600] || '#4b5563' }}>Subtotal</span>
                          <span style={{ color: theme.text }}>{currencySymbol}{invoiceData.subtotal.toFixed(2)}</span>
                        </div>
                      )}

                      {/* Discount — shown before taxes/service charge because it is
                          applied first: taxes and service charge are computed on the
                          discounted base (display order mirrors calculation order) */}
                      {invoiceData.discountAmount !== undefined && invoiceData.discountAmount !== null && invoiceData.discountAmount > 0 && (
                        <div className="rounded-lg p-2 mt-2" style={{ backgroundColor: `${theme.success}10`, border: `1px solid ${theme.success}25` }}>
                          <div className="flex justify-between text-sm">
                            <span className="flex items-center gap-1" style={{ color: theme.gray?.[600] || '#4b5563' }}>
                              <span className="text-lg">🏷️</span>
                              Discount
                              {invoiceData.discountType && (
                                <span className="text-xs px-1.5 py-0.5 rounded ml-1" style={{ backgroundColor: `${theme.success}20`, color: theme.success }}>
                                  {invoiceData.discountType === 'PERCENTAGE'
                                    ? `${invoiceData.discountValue}%`
                                    : invoiceData.discountType === 'COUPON'
                                      ? `Code: ${invoiceData.couponCode || 'Applied'}`
                                      : 'Fixed'}
                                </span>
                              )}
                            </span>
                            <span className="font-semibold" style={{ color: theme.success }}>-{currencySymbol}{invoiceData.discountAmount.toFixed(2)}</span>
                          </div>
                          {invoiceData.discountReason && (
                            <p className="text-xs mt-1 italic" style={{ color: theme.gray?.[500] || '#6b7280' }}>
                              Reason: {invoiceData.discountReason}
                            </p>
                          )}
                        </div>
                      )}

                      {/* CGST */}
                      {invoiceData.cgstAmount !== undefined && invoiceData.cgstAmount !== null && invoiceData.cgstAmount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span style={{ color: theme.gray?.[600] || '#4b5563' }}>
                            CGST {invoiceData.cgstRate ? `(${invoiceData.cgstRate}%)` : ''}
                          </span>
                          <span style={{ color: theme.text }}>{currencySymbol}{invoiceData.cgstAmount.toFixed(2)}</span>
                        </div>
                      )}

                      {/* SGST */}
                      {invoiceData.sgstAmount !== undefined && invoiceData.sgstAmount !== null && invoiceData.sgstAmount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span style={{ color: theme.gray?.[600] || '#4b5563' }}>
                            SGST {invoiceData.sgstRate ? `(${invoiceData.sgstRate}%)` : ''}
                          </span>
                          <span style={{ color: theme.text }}>{currencySymbol}{invoiceData.sgstAmount.toFixed(2)}</span>
                        </div>
                      )}

                      {/* IGST */}
                      {invoiceData.igstAmount !== undefined && invoiceData.igstAmount !== null && invoiceData.igstAmount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span style={{ color: theme.gray?.[600] || '#4b5563' }}>
                            IGST {invoiceData.igstRate ? `(${invoiceData.igstRate}%)` : ''}
                          </span>
                          <span style={{ color: theme.text }}>{currencySymbol}{invoiceData.igstAmount.toFixed(2)}</span>
                        </div>
                      )}

                      {/* Service Charge */}
                      {invoiceData.serviceChargeAmount !== undefined && invoiceData.serviceChargeAmount !== null && invoiceData.serviceChargeAmount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span style={{ color: theme.gray?.[600] || '#4b5563' }}>
                            Service Charge {invoiceData.serviceChargeRate ? `(${invoiceData.serviceChargeRate}%)` : ''}
                          </span>
                          <span style={{ color: theme.text }}>{currencySymbol}{invoiceData.serviceChargeAmount.toFixed(2)}</span>
                        </div>
                      )}

                      {/* Round Off (when present) */}
                      {invoiceData.roundOff !== undefined && invoiceData.roundOff !== null && Math.abs(invoiceData.roundOff) >= 0.01 && (
                        <div className="flex justify-between text-sm">
                          <span style={{ color: theme.gray?.[600] || '#4b5563' }}>Round Off</span>
                          <span style={{ color: theme.text }}>
                            {invoiceData.roundOff >= 0 ? '+' : '-'}{currencySymbol}{Math.abs(invoiceData.roundOff).toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* Total — prefer backend-rounded grandTotal (single source of truth) */}
                      <div className="flex justify-between items-center pt-2 border-t" style={{ borderColor: theme.components?.card?.border || '#E8E6E1' }}>
                        <p className="text-base font-semibold" style={{ color: theme.text }}>Total</p>
                        <p className="text-lg font-bold" style={{ color: theme.text }}>
                          {currencySymbol}{(() => {
                            // Backend returns the authoritative grandTotal: whole rupees
                            // for INR, exact pence for GBP (UK plan Phase 2 step 6)
                            if (typeof invoiceData.grandTotal === 'number') {
                              return isWholeUnitRounding
                                ? invoiceData.grandTotal.toFixed(0)
                                : invoiceData.grandTotal.toFixed(2);
                            }
                            // Legacy fallback: recompute and round
                            const subtotal = invoiceData.subtotal || 0;
                            const cgst = invoiceData.cgstAmount || 0;
                            const sgst = invoiceData.sgstAmount || 0;
                            const igst = invoiceData.igstAmount || 0;
                            const serviceCharge = invoiceData.serviceChargeAmount || 0;
                            const discount = invoiceData.discountAmount || 0;
                            const calculatedTotal = subtotal + cgst + sgst + igst + serviceCharge - discount;
                            return isWholeUnitRounding
                              ? Math.round(calculatedTotal).toString()
                              : calculatedTotal.toFixed(2);
                          })()}
                        </p>
                      </div>

                      {/* Includes VAT (GB tax-inclusive invoices) — informational,
                          the VAT is inside the total, never added on top */}
                      {typeof invoiceData.vatAmount === 'number' && invoiceData.vatAmount > 0 && (
                        <div className="flex justify-between text-xs">
                          <span style={{ color: theme.gray?.[500] || '#6b7280' }}>
                            Includes VAT{invoiceData.vatRate ? ` (${invoiceData.vatRate}%)` : ''}
                          </span>
                          <span style={{ color: theme.gray?.[500] || '#6b7280' }}>
                            {currencySymbol}{invoiceData.vatAmount.toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* Payment Method */}
                      {invoiceData.paymentMethod && (
                        <div className="flex justify-between items-center pt-2 mt-2 border-t" style={{ borderColor: theme.components?.card?.border || '#E8E6E1' }}>
                          <p className="text-sm" style={{ color: theme.gray?.[600] || '#4b5563' }}>Payment Method</p>
                          <p className="text-sm font-medium flex items-center gap-1" style={{ color: theme.text }}>
                            <span>
                              {invoiceData.paymentMethod === 'CASH' && '💵'}
                              {invoiceData.paymentMethod === 'CARD' && '💳'}
                              {invoiceData.paymentMethod === 'UPI' && '📱'}
                              {invoiceData.paymentMethod === 'WALLET' && '👛'}
                              {invoiceData.paymentMethod === 'MOBILE_PAY' && '📲'}
                              {invoiceData.paymentMethod === 'GIFT_CARD' && '🎁'}
                              {invoiceData.paymentMethod === 'OTHER' && '📝'}
                            </span>
                            {invoiceData.paymentMethod}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                  {/* Bottom padding for mobile navigation */}
                  <div className="h-16 lg:h-4"></div>
              </Box>
            </>
          )}
        </div>
      </div>
    </Modal>
    </>
  );
};

export default InvoiceModal;
